/** @format */

import {useState} from 'react';

import {Box, CircularProgress} from '@mui/material';
import {Trans} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {TanStackTableData} from 'components/Table/core/types';
import BaseSearchModal from 'components/BaseSearchModal';

import {DuplicateForm} from 'pages/Home/Project/Steps/Sensors/components/DuplicateForm';

import ErrorBlock from 'components/ErrorBlock';

import {StyledButton} from './styles';

interface AnalyticGoalsProps {
  data: TanStackTableData[];
  selectedGoalIds: string[];
  onGoalsConfirm: (ids: string[]) => void;
  isError: boolean;
  isLoading?: boolean;
}

const GOAL_COLUMNS = [
  {id: 'counterId', title: 'ID Метрики'},
  {id: 'counterName', title: 'Название Метрики'},
  {id: 'id', title: 'ID цели'},
  {id: 'name', title: 'Название цели'},
];

const SEARCH_BY_COLUMN_IDS = GOAL_COLUMNS.map(col => col.id);
const MAX_SELECTIONS = 10;

const AnalyticGoals = ({
  data,
  selectedGoalIds,
  isError,
  isLoading = false,
  onGoalsConfirm,
}: AnalyticGoalsProps) => {
  const [isOpenContactModal, setIsOpenContactModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId') || '';

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <ErrorBlock
        title='Не удается получить цели из Яндекс Метрики'
        description='Переподключите Яндекс Метрику в настройках проекта. Если ошибка повторяется — напишите нам.'
        additionalInfo={
          <>
            <StyledButton
              variant='contained'
              color='primary'
              onClick={() => setIsOpenContactModal(true)}
            >
              <Trans>Связаться с менеджером</Trans>
            </StyledButton>
            {isOpenContactModal && (
              <DuplicateForm
                companyId={companyId}
                title='Связаться с менеджером'
                subtitle='Чтобы ускорить процесс, свяжитесь с менеджером и он поможет вам настроить цели'
                boxNote={null}
                onClose={() => setIsOpenContactModal(false)}
              />
            )}
          </>
        }
      />
    );
  }

  if (data.length === 0) {
    return (
      <ErrorBlock
        title='В Яндекс Метрике отсутствуют цели'
        description='В подключенной Яндекс Метрике нет целей. Чтобы отслеживать показатели - создайте цели и выберите их в настройках проекта. Или подключите другой аккаунт Яндекс Метрике, где цели уже настроены.'
      />
    );
  }

  return (
    <>
      <StyledButton variant='outlined' size='small' onClick={() => setIsModalOpen(true)}>
        <Trans>Изменить цели на проект</Trans>
      </StyledButton>
      <BaseSearchModal
        columns={GOAL_COLUMNS}
        data={data}
        modalTitle='Выбор целей'
        searchPlaceholder='Поиск по названию или ID'
        confirmButtonText='Подтвердить'
        searchByColumnIds={SEARCH_BY_COLUMN_IDS}
        initialSelectedIds={selectedGoalIds}
        maxSelections={MAX_SELECTIONS}
        needToConnectMetrika={false}
        onConfirm={onGoalsConfirm}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        getDisplayLabel={(item: TanStackTableData) => String(item.name || item.id || '')}
      />
    </>
  );
};

export default AnalyticGoals;
